import type { HTMLAttributes, ReactNode, TableHTMLAttributes } from 'react';
import { HpIcon, Icon, type IconName } from './icons.js';

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/** `.hp-page-frame` — the page root; the shell owns width and outer padding. */
export function PageFrame({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx('hp-page-frame', className)} {...rest}>
      {children}
    </div>
  );
}

export interface PageHeadingProps {
  title: ReactNode;
  /** One sentence under the title. */
  description?: ReactNode;
  /** Right-hand actions (text button + primary button). */
  actions?: ReactNode;
  /** Small record id shown after the title (project/record pages). */
  recordId?: ReactNode;
  /** Extra line under the title (tags, meta). */
  meta?: ReactNode;
  className?: string;
}

/** `.page-heading.hp-page-heading` — h1 + description left, `.heading-actions` right. */
export function PageHeading({ title, description, actions, recordId, meta, className }: PageHeadingProps) {
  return (
    <div className={cx('page-heading', 'hp-page-heading', className)}>
      <div>
        <h1>
          {title}
          {recordId !== undefined && <span className="bo-record-id">{recordId}</span>}
        </h1>
        {description && <p>{description}</p>}
        {meta}
      </div>
      {actions && <div className="heading-actions">{actions}</div>}
    </div>
  );
}

/** `.hp-page-grid` — main column + optional work rail (360/330 px). */
export function PageGrid({
  children,
  aside,
  className,
}: {
  children: ReactNode;
  aside?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('hp-page-grid', className)}>
      <div>{children}</div>
      {aside && <aside>{aside}</aside>}
    </div>
  );
}

// --- cards and panels ---

/** `.hp-card` — 1 px line, radius 13, no shadow. */
export function HpCard({ className, children, ...rest }: HTMLAttributes<HTMLElement>) {
  return (
    <section className={cx('hp-card', className)} {...rest}>
      {children}
    </section>
  );
}

export interface HpCardHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right side: tags, actions. */
  children?: ReactNode;
  /** Section icon rendered before the title. */
  icon?: IconName;
  className?: string;
}

/** `.hp-card__header` — h2 (+ subtitle) left, actions right. */
export function HpCardHeader({ title, subtitle, icon, children, className }: HpCardHeaderProps) {
  return (
    <header className={cx('hp-card__header', className)}>
      <div>
        <h2 className="bo-card-title">
          {icon && <HpIcon name={icon} />}
          {title}
        </h2>
        {subtitle && <p className="bo-card-subtitle">{subtitle}</p>}
      </div>
      {children && <div className="bo-card-actions">{children}</div>}
    </header>
  );
}

export function HpCardBody({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx('hp-card__body', className)} {...rest}>
      {children}
    </div>
  );
}

/** `.panel` — legacy detail panel (23 px padding, radius 11). */
export function Panel({ className, children, ...rest }: HTMLAttributes<HTMLElement>) {
  return (
    <section className={cx('panel', className)} {...rest}>
      {children}
    </section>
  );
}

/** `.panel-header` — h2 + trailing content. */
export function PanelHeader({ title, icon, children }: { title: ReactNode; icon?: IconName; children?: ReactNode }) {
  return (
    <div className="panel-header">
      <h2>
        {icon && <Icon name={icon} />}
        {title}
      </h2>
      {children}
    </div>
  );
}

export function SectionDivider() {
  return <div className="section-divider" />;
}

/** `.stack` — vertical rhythm of 20 px between panels. */
export function Stack({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx('stack', className)} {...rest}>
      {children}
    </div>
  );
}

// --- switches ---

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
}

/** `.hp-segmented` — one pattern for tabs, view switches and section navigation; active = lime. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  className,
}: {
  value: T;
  options: Array<SegmentedOption<T>>;
  onChange: (value: T) => void;
  ariaLabel: string;
  className?: string;
}) {
  return (
    <div className={cx('hp-segmented', className)} role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          disabled={o.disabled}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// --- dashboard cards (view switch) ---

export interface DashboardCardProps {
  icon: IconName;
  value: ReactNode;
  label: ReactNode;
  active?: boolean;
  onClick?: () => void;
  /** Renders as a link when given. */
  href?: string;
}

/** `.hp-dashboard-card` — count + label with an icon, lime when active. */
export function DashboardCard({ icon, value, label, active, onClick }: DashboardCardProps) {
  return (
    <button
      type="button"
      className={cx('hp-dashboard-card', active && 'is-active')}
      aria-pressed={active}
      onClick={onClick}
    >
      <span className="bo-dashboard-icon">
        <HpIcon name={icon} />
      </span>
      <span className="bo-dashboard-copy">
        <strong>{value}</strong>
        <span>{label}</span>
      </span>
    </button>
  );
}

export function DashboardGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('hp-dashboard-grid', className)}>{children}</div>;
}

// --- tables ---

/** `.table-scroll[tabindex=0]` — wide tables overflow inside; a scroll hint appears under 800 px. */
export function TableScroll({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('table-scroll', className)} tabIndex={0} aria-label={label}>
      {children}
    </div>
  );
}

/** `.hp-table` — 60 px rows, uppercase header, tabular numerals. */
export function HpTable({ className, children, ...rest }: TableHTMLAttributes<HTMLTableElement>) {
  return (
    <table className={cx('hp-table', className)} {...rest}>
      {children}
    </table>
  );
}

/** `.table-footer` — "1–8 of 12" left, pagination right. */
export function TableFooter({ children, pagination }: { children: ReactNode; pagination?: ReactNode }) {
  return (
    <div className="table-footer">
      <span>{children}</span>
      {pagination && <div className="pagination">{pagination}</div>}
    </div>
  );
}

// --- metrics ---

export interface RecordMetricProps {
  icon: IconName;
  value: ReactNode;
  label: ReactNode;
  /** Renders as a link when given. */
  href?: string;
  onClick?: () => void;
}

/** `.record-metric` — white card with icon, 25 px number and a label. */
export function RecordMetric({ icon, value, label, href, onClick }: RecordMetricProps) {
  const body = (
    <>
      <span className="record-metric-icon">
        <Icon name={icon} />
      </span>
      <span>
        <strong>{value}</strong>
        <small>{label}</small>
      </span>
    </>
  );
  if (href) {
    return (
      <a className="record-metric" href={href} onClick={onClick}>
        {body}
      </a>
    );
  }
  return <div className="record-metric">{body}</div>;
}

export function RecordMetrics({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('record-metrics', className)}>{children}</div>;
}
